import {
  Archive,
  BarChart10,
  Bell01,
  BellOff01,
  Copy01,
  CurrencyDollarCircle,
  Edit03,
  LinkExternal01,
  Pin01,
  Pin02 as PinOff,
  Trash01,
} from '@timelyapp/icons/solid-icons';
import type { DropdownMenuItem } from '@timelyapp/react';
import { useWithDispatch } from 'hooks/useWithDispatch';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createInvoiceDraft } from 'redux/modules/invoice.actions';
import { togglePinOnProjectId } from 'redux/modules/pins.actions';
import { subscribeToProject, unsubscribeFromProject } from 'redux/modules/projectSubscriptions';
import {
  activateProjectId,
  editCopyOfProjectId,
  editProjectId,
} from 'redux/modules/projects.actions';
import { showSubscriptionNoticeModal } from 'redux/modules/subscriptions.actions';
import type { ModalMessageKeyTypes } from 'redux/modules/subscriptions.types';
import { createReportDraft } from 'redux/modules/superreports.actions';
import type { TicMeta } from 'types/tic';
import { ucFirst } from 'utils/text-helper';

// Project props needed for the dropdown
export interface ProjectDropdownProps {
  projectId: number;
  projectName: string;
  isPinned?: boolean;
  isActive?: boolean;
  isProjectsDashboardFeatureLocked?: boolean;
  showSubscribe?: boolean;
  showUnsubscribe?: boolean;
  isSubscribeLockedFeature?: boolean;
  permissions: {
    canCreate: {
      projects?: boolean;
      reports?: boolean;
      invoices?: boolean;
    };
    canDelete: {
      projects?: boolean;
    };
    canUpdate?: {
      projects?: boolean;
    };
  };
  features: {
    invoices?: boolean;
  };
  onProjectActionRequest?: (action: 'delete' | 'archive', id: number, name: string) => void;
  reportDraftSeed?: any;
  tic?: TicMeta;
}

/**
 * Hook that provides dropdown menu items for project options, keeping the Redux dispatching
 * but removing the direct store reading
 */
const useProjectOptionsDropdown = ({
  projectId,
  projectName,
  isPinned = false,
  isActive = true,
  isProjectsDashboardFeatureLocked = false,
  showSubscribe = false,
  showUnsubscribe = false,
  isSubscribeLockedFeature = false,
  permissions = { canCreate: {}, canDelete: {} },
  features = {},
  onProjectActionRequest,
  reportDraftSeed,
  tic,
}: ProjectDropdownProps) => {
  const navigate = useNavigate();

  const actions = useWithDispatch({
    editProjectId,
    createReportDraft,
    activateProjectId,
    togglePinOnProjectId,
    subscribeToProject,
    unsubscribeFromProject,
    showSubscriptionNoticeModal,
    editCopyOfProjectId,
    createInvoiceDraft,
  });

  const dropdownItems = useMemo<DropdownMenuItem[]>(() => {
    const items: DropdownMenuItem[] = [];

    // Edit option - add to dropdown in list views but not on details page
    // The project details page has a separate Edit button
    if (permissions.canUpdate?.projects) {
      items.push({
        type: 'item',
        key: 'edit',
        label: 'Edit',
        icon: <Edit03 />,
        onSelect: () => actions.editProjectId(projectId),
      });
    }

    // Pin/Unpin option
    if (!isProjectsDashboardFeatureLocked) {
      items.push(
        isPinned
          ? {
              type: 'item',
              key: 'unpin',
              label: 'Unpin from dashboard',
              icon: <PinOff />,
              onSelect: () => actions.togglePinOnProjectId(projectId),
            }
          : {
              type: 'item',
              key: 'pin',
              label: 'Pin to dashboard',
              icon: <Pin01 />,
              onSelect: () => actions.togglePinOnProjectId(projectId),
            }
      );
    }

    // Duplicate option
    if (permissions.canCreate.projects) {
      items.push({
        type: 'item',
        key: 'duplicate',
        label: 'Duplicate',
        icon: <Copy01 />,
        onSelect: () => actions.editCopyOfProjectId(projectId),
      });
    }

    items.push({ type: 'separator' });

    // Open in Report option
    if (permissions.canCreate.reports) {
      items.push({
        type: 'item',
        key: 'openReport',
        label: 'Open in Report',
        icon: <BarChart10 />,
        onSelect: () => {
          if (reportDraftSeed) {
            actions.createReportDraft(reportDraftSeed);
          }
          navigate(`/superreports/new?project_id=${projectId}`);
        },
      });
    }

    // Generate invoice option
    if (permissions.canCreate.invoices && features.invoices) {
      items.push({
        type: 'item',
        key: 'generateInvoice',
        label: 'Generate invoice',
        icon: <CurrencyDollarCircle />,
        onSelect: () => actions.createInvoiceDraft({ filters: { project_ids: [projectId] } }),
      });
    }

    // Subscribe option
    if (showSubscribe) {
      items.push({
        type: 'item',
        key: 'subscribe',
        label: 'Subscribe',
        icon: <Bell01 />,
        disabled: isSubscribeLockedFeature,
        onSelect: () => {
          if (isSubscribeLockedFeature) {
            actions.showSubscriptionNoticeModal(
              'featureProjectNotificationsRequiresUpgrade' as ModalMessageKeyTypes
            );
          } else {
            actions.subscribeToProject(projectId);
          }
        },
      });
    }

    // Unsubscribe option
    if (showUnsubscribe) {
      items.push({
        type: 'item',
        key: 'unsubscribe',
        label: 'Unsubscribe',
        icon: <BellOff01 />,
        onSelect: () => actions.unsubscribeFromProject(projectId),
      });
    }

    // Delete option
    if (permissions.canDelete.projects) {
      items.push({ type: 'separator' });

      // Activate/Archive option
      if (!isActive) {
        items.push({
          type: 'item',
          key: 'activate',
          label: 'Restore',
          icon: <Archive />,
          onSelect: () => actions.activateProjectId(projectId),
        });
      } else {
        items.push({
          type: 'item',
          key: 'archive',
          label: 'Archive',
          variant: 'alert',
          icon: <Archive />,
          onSelect: () => {
            if (onProjectActionRequest) {
              onProjectActionRequest('archive', projectId, projectName);
            }
          },
        });
      }

      items.push({
        type: 'item',
        key: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: <Trash01 />,
        onSelect: () => {
          if (onProjectActionRequest) {
            onProjectActionRequest('delete', projectId, projectName);
          }
        },
      });
    }

    // Visit in external tool option (for TIC-synced projects)
    if (tic?.external_url) {
      const toolName = tic.tool_id ? ucFirst(tic.tool_id) : 'external tool';
      items.push({ type: 'separator' });
      items.push({
        type: 'item',
        key: 'visitExternal',
        label: `Visit in ${toolName}`,
        icon: tic.icon ? (
          <img src={tic.icon} alt="" style={{ width: 14, height: 14 }} />
        ) : (
          <LinkExternal01 />
        ),
        onSelect: () => window.open(tic.external_url, '_blank', 'noopener,noreferrer'),
      });
    }

    return items;
  }, [
    tic,
    isProjectsDashboardFeatureLocked,
    showSubscribe,
    showUnsubscribe,
    isSubscribeLockedFeature,
    permissions.canCreate.projects,
    permissions.canCreate.reports,
    permissions.canCreate.invoices,
    permissions.canDelete.projects,
    permissions.canUpdate,
    features.invoices,
    isPinned,
    isActive,
    projectName,
    actions,
    projectId,
    navigate,
    onProjectActionRequest,
    reportDraftSeed,
  ]);

  // Return ONLY dropdown items, no longer exposing editProject function
  return { dropdownItems };
};

export default useProjectOptionsDropdown;
